import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  Send,
  MessageSquare,
  Mail,
  Link2,
  Check,
  X
} from "lucide-react";

interface CosmicSharingProps {
  language: "en" | "hr";
}

export default function CosmicSharing({ language }: CosmicSharingProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const getShareUrl = () => {
    if (typeof window !== "undefined") {
      return window.location.href;
    }
    return "https://pleiades-m45.com";
  };

  const shareTitle = language === "en"
    ? "Pleiades M45 • Celestial Intelligence & Sacred Geometry"
    : "Pleiades M45 • Kozmička Inteligencija i Sveta Geometrija";

  const shareText = language === "en"
    ? "Discover the mysteries of the Seven Sisters, frequency physics, sacred geometry and live star cluster telemetry."
    : "Otkrijte tajne Sedam Sestara, frekvencijsku fiziku, svetu geometriju i telemetriju zvjezdanog skupa.";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setShowToast(true);
      setTimeout(() => setCopied(false), 2000);
      setTimeout(() => setShowToast(false), 3500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const shareLinks = [
    {
      name: "X / Twitter",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(shareTitle + " - " + shareText)}`,
      color: "hover:bg-white/10 hover:text-white border-white/10",
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`,
      color: "hover:bg-[#1877F2]/20 hover:text-[#1877F2] border-[#1877F2]/20",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`,
      color: "hover:bg-[#0077B5]/20 hover:text-[#0077B5] border-[#0077B5]/20",
    },
    {
      name: "WhatsApp",
      icon: MessageSquare,
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + "\n" + shareText + "\n" + getShareUrl())}`,
      color: "hover:bg-[#25D366]/20 hover:text-[#25D366] border-[#25D366]/20",
    },
    {
      name: "Telegram",
      icon: Send,
      href: `https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(shareTitle + " - " + shareText)}`,
      color: "hover:bg-[#0088cc]/20 hover:text-[#0088cc] border-[#0088cc]/20",
    },
    {
      name: "Reddit",
      icon: Share2,
      href: `https://www.reddit.com/submit?url=${encodeURIComponent(getShareUrl())}&title=${encodeURIComponent(shareTitle)}`,
      color: "hover:bg-[#FF4500]/20 hover:text-[#FF4500] border-[#FF4500]/20",
    },
    {
      name: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + "\n\n" + getShareUrl())}`,
      color: "hover:bg-quantum-turquoise/20 hover:text-quantum-turquoise border-quantum-turquoise/20",
    },
  ];

  return (
    <>
      {/* FLOATING ACTION TRIGGER */}
      <div className="fixed bottom-24 right-4 md:right-6 z-40 flex flex-col items-end">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 12 }}
              className="mb-3 bg-cosmic-black/95 backdrop-blur-xl border border-quantum-turquoise/35 rounded-2xl p-4 md:p-5 shadow-[0_0_30px_rgba(0,212,255,0.15)] max-w-[280px] w-64 text-center relative"
            >
              {/* HUD corner lines */}
              <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-quantum-turquoise/40"></div>
              <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-quantum-turquoise/40"></div>
              <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-quantum-turquoise/40"></div>
              <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-quantum-turquoise/40"></div>

              <div className="flex items-center justify-between border-b border-quantum-turquoise/20 pb-2.5 mb-3">
                <span className="text-[10px] font-mono tracking-[0.2em] text-quantum-turquoise uppercase font-bold flex items-center gap-1.5">
                  <Share2 className="w-3 h-3 animate-pulse" />
                  {language === "en" ? "CELESTIAL SHARE" : "KOSMIČKO DIJELJENJE"}
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <p className="text-[10px] text-gray-400 font-sans mb-3 text-left leading-relaxed">
                {language === "en"
                  ? "Broadcast this cosmic library to other sectors and networks."
                  : "Prenesite ove kozmičke zapise u druge sektore i mreže."}
              </p>

              {/* Share grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                {shareLinks.map((link) => {
                  const IconComponent = link.icon;
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center justify-center p-2 rounded-lg border bg-cosmic-indigo/20 transition-all duration-300 ${link.color} group relative`}
                      title={link.name}
                    >
                      <IconComponent className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 bg-cosmic-black text-[8px] font-mono rounded border border-quantum-turquoise/35 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                        {link.name}
                      </span>
                    </a>
                  );
                })}
              </div>

              {/* Copy Link Button */}
              <button
                onClick={handleCopyLink}
                className={`w-full py-2 px-3 rounded-lg border font-mono text-[10px] uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-300 ${
                  copied
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                    : "bg-quantum-turquoise/10 text-quantum-turquoise border-quantum-turquoise/25 hover:bg-quantum-turquoise/20 hover:border-quantum-turquoise/40"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    {language === "en" ? "Copied!" : "Kopirano!"}
                  </>
                ) : (
                  <>
                    <Link2 className="w-3.5 h-3.5" />
                    {language === "en" ? "Copy Portal Link" : "Kopiraj portal link"}
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trigger FAB Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all duration-500 shadow-[0_0_15px_rgba(0,212,255,0.3)] hover:shadow-[0_0_25px_rgba(0,212,255,0.65)] hover:scale-105 active:scale-95 ${
            isOpen
              ? "bg-quantum-turquoise text-cosmic-black border-quantum-turquoise rotate-90"
              : "bg-cosmic-black/90 text-quantum-turquoise border-quantum-turquoise/40 hover:border-quantum-turquoise hover:bg-quantum-turquoise/5"
          }`}
          title={language === "en" ? "Share Portal" : "Podijeli portal"}
        >
          {isOpen ? (
            <X className="w-5 h-5 transition-transform duration-300" />
          ) : (
            <Share2 className="w-5 h-5 transition-transform duration-300" />
          )}
        </button>
      </div>

      {/* SUCCESS TOAST NOTIFICATION */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 20, x: "-50%" }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-3 bg-cosmic-black/90 backdrop-blur-md border border-emerald-500/40 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.35)] flex items-center gap-3 font-mono text-xs text-emerald-400"
          >
            <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
              <Check className="w-3 h-3 text-emerald-400" />
            </div>
            <div>
              <span className="font-bold uppercase tracking-wider block">
                {language === "en" ? "SYNC COMPLETE" : "SINKRONIZACIJA USPJELA"}
              </span>
              <span className="text-[10px] text-gray-300">
                {language === "en"
                  ? "Cosmic coordinate link saved to clipboard."
                  : "Kozmička poveznica spremljena u međuspremnik."}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
